const IMAGE_SIZE_STEPS = [150, 300, 500, 750, 1024, 2048];

// Returns the smallest step size that can accommodate the given dimension, or null for original
function getStepSize(dimension) {
  for (let i = 0; i < IMAGE_SIZE_STEPS.length; i++) {
    const step = IMAGE_SIZE_STEPS[i];

    if (dimension <= step) {
      return IMAGE_SIZE_STEPS[i + 1] || null;
    }
  }
  return null; // Exceeds all steps, use original
}

export function useSurfaceEditorImageRequest({
 image,
 renderScale
}) {
  const surfaceScale = computed(() => unref(renderScale) ?? 1);

  const imageSize = computed(() => {
    const imageValue = unref(image);
    const baseWidth = imageValue.image_width * imageValue.scale;
    const baseHeight = imageValue.image_height * imageValue.scale;

    return {
      width: Math.max(1, Math.round(baseWidth * surfaceScale.value)),
      height: Math.max(1, Math.round(baseHeight * surfaceScale.value))
    };
  });

  // The step size needed for the current rendered dimensions
  const requiredStepSize = computed(() => {
    const maxDimension = Math.max(imageSize.value.width, imageSize.value.height);
    return getStepSize(maxDimension);
  });

  const imageSourceUrl = computed(() => {
    const imageValue = unref(image);

    if (surfaceScale.value >= 1) {
      return imageValue.url;
    }

    if (requiredStepSize.value === null) {
      return imageValue.url;
    }

    return imageValue.working_url || imageValue.url;
  });

  // Track the highest step size we've loaded to avoid requesting smaller images
  // undefined = not yet loaded, null = original (largest), number = step size
  const highestLoadedStep = ref(undefined);
  const currentStepSize = ref(requiredStepSize.value);
  const currentImageSourceUrl = ref(imageSourceUrl.value);

  // Track the base image URL to detect when the image is replaced entirely
  const baseImageUrl = computed(() => unref(image).url);
  let lastBaseImageUrl = baseImageUrl.value;

  watch([requiredStepSize, imageSourceUrl, baseImageUrl], ([stepSize, sourceUrl, newBaseUrl]) => {
    // Reset progressive loading state when image is replaced
    if (newBaseUrl !== lastBaseImageUrl) {
      lastBaseImageUrl = newBaseUrl;
      highestLoadedStep.value = undefined;
    }

    // Only update if the new step is larger than what we've already loaded (progressive loading)
    const currentHighest = highestLoadedStep.value;

    let shouldUpdate = false;

    if (currentHighest === undefined) {
      // First load - always update
      shouldUpdate = true;
    } else if (currentHighest === null) {
      // Already at original (largest), don't downgrade
      shouldUpdate = false;
    } else if (stepSize === null) {
      // Requesting original - always the largest
      shouldUpdate = true;
    } else if (stepSize > currentHighest) {
      // Need a larger step size
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      currentStepSize.value = stepSize;
      highestLoadedStep.value = stepSize;
    }

    // Always update source URL
    currentImageSourceUrl.value = sourceUrl;
  }, {immediate: true});

  const imageUrl = computed(() => {
    const imageValue = unref(image);

    // If surfaceScale >= 1 or step is null (exceeds max step), load original
    if (surfaceScale.value >= 1 || currentStepSize.value === null) {
      return updateQueryParameters(imageValue.url, {
        format: 'jpg',
        width: null,
        height: null
      });
    }

    // Use the stepped size for the largest dimension, let the server scale proportionally
    const aspectRatio = imageValue.image_width / imageValue.image_height;
    const isWidthLarger = aspectRatio >= 1;

    return updateQueryParameters(currentImageSourceUrl.value, {
      format: 'jpg',
      width: isWidthLarger ? currentStepSize.value : null,
      height: isWidthLarger ? null : currentStepSize.value
    });
  });

  return {
    imageUrl,
    imageSize,
    imageSourceUrl,
    currentStepSize,
    currentImageSourceUrl
  };
}
